import create from 'zustand'
import {
    EPISODE_MODE_ALL,
    reconcileEpisodeSelection as reconcileEpisodeSelectionState,
    transitionEpisodeSelection,
} from '../utils/episodeSelection';
import {
    acknowledgeEpisodeSelectionTransaction,
    beginEpisodeSelectionTransaction,
    registerEpisodeSelectionConsumerState,
    unregisterEpisodeSelectionConsumerState,
} from '../utils/episodeSelectionCoordinator';
import { getDefaultWeekInterval, getPresetWeekInterval, normalizeWeekRange } from '../utils/weekInterval';

function parseJwt(token) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

const initState = {
    isAuth: localStorage.getItem('isAuth') === 'true' || false,
    access: localStorage.getItem('access') || "",
    refresh: localStorage.getItem('refresh') || "",
    alert: false,
    isAdmin: localStorage.getItem('isAdmin') === 'true' || false,
    username: localStorage.getItem('username') || "",
    podcast: {},
    podcasts: [],
    episodes: [],
    scopes: JSON.parse(localStorage.getItem("scopes")),
    activeSection: 'graphs',
    episodeMode: EPISODE_MODE_ALL,
    selectedEpisodeIds: [],
    episodeSelectionRevision: 0,
    episodeSelectionBusy: null,
    registeredEpisodeSelectionConsumers: [],
    collectedStartWeek: '',
    collectedEndWeek: '',
    ...getDefaultWeekInterval()
}

export const useAppState = create((set) => ({
    ...initState,
    setAlert: ({severity, message}) => set((state) => ({ ...state, alert: {severity, message} })),
    clearAlert: () => set((state) => ({ ...state, alert: false })),
    login: ({access, refresh, admin, username}) => set(
        (state) => {
            console.log("Logging in");

            let scopes = parseJwt(access)?.scopes
            localStorage.setItem('access', access);
            localStorage.setItem('refresh', refresh);
            localStorage.setItem('isAuth', true);
            localStorage.setItem('isAdmin', admin);
            localStorage.setItem('username', username);
            localStorage.setItem('scopes', JSON.stringify(scopes))
            
            return {
                ...state,
                isAuth: true,
                isAdmin: admin,
                access: access,
                refresh: refresh,
                username: username,
                scopes: scopes
              }
        }
    ),
    logout: () => set((state) => {
            console.log("Logging out");
            localStorage.clear();
            console.log("Clearing storage");
            return {
                ...initState,
                isAuth: false,
                alert: state.alert,
                episodeSelectionRevision: state.episodeSelectionRevision + 1,
                registeredEpisodeSelectionConsumers: [],
            }
        }),
    setPodcast: (podcast) => set((state) => ({
        ...state,
        podcast,
        episodeMode: EPISODE_MODE_ALL,
        selectedEpisodeIds: [],
        episodeSelectionRevision: state.episodeSelectionRevision + 1,
        episodeSelectionBusy: null,
    })),
    setPodcasts: (podcasts) => set((state) => ({...state, podcasts})),
    setEpisodes: (episodes) => set((state) => ({...state, episodes})),
    setCollectedWeekRange: ({startWeek, endWeek}) => set((state) => ({
        ...state,
        collectedStartWeek: startWeek || state.collectedStartWeek,
        collectedEndWeek: endWeek || state.collectedEndWeek,
    })),
    clearCollectedWeekRange: () => set((state) => ({...state, collectedStartWeek: '', collectedEndWeek: ''})),
    setActiveSection: (activeSection) => set((state) => {
        if (activeSection === state.activeSection) {
            return state;
        }

        return {
            ...state,
            activeSection,
            episodeSelectionRevision: state.episodeSelectionRevision + 1,
            episodeSelectionBusy: null,
        };
    }),
    applyEpisodeSelection: ({action, availableEpisodeIds, target}) => set((state) => {
        const nextSelection = transitionEpisodeSelection({
            mode: state.episodeMode,
            selectedEpisodeIds: state.selectedEpisodeIds,
        }, action, availableEpisodeIds);
        const transaction = beginEpisodeSelectionTransaction({
            revision: state.episodeSelectionRevision,
            registeredConsumerIds: state.registeredEpisodeSelectionConsumers,
        }, target);

        return {
            ...state,
            episodeMode: nextSelection.mode,
            selectedEpisodeIds: nextSelection.selectedEpisodeIds,
            episodeSelectionRevision: transaction.revision,
            episodeSelectionBusy: transaction.busy,
        };
    }),
    reconcileEpisodeSelection: (availableEpisodeIds) => set((state) => {
        const nextSelection = reconcileEpisodeSelectionState({
            mode: state.episodeMode,
            selectedEpisodeIds: state.selectedEpisodeIds,
        }, availableEpisodeIds);

        if (
            nextSelection.mode === state.episodeMode &&
            nextSelection.selectedEpisodeIds.join(',') === state.selectedEpisodeIds.join(',')
        ) {
            return state;
        }

        return {
            ...state,
            ...nextSelection,
            episodeSelectionRevision: state.episodeSelectionRevision + 1,
            episodeSelectionBusy: null,
        };
    }),
    registerEpisodeSelectionConsumer: (consumerId) => set((state) => ({
        ...state,
        registeredEpisodeSelectionConsumers: registerEpisodeSelectionConsumerState(
            state.registeredEpisodeSelectionConsumers,
            consumerId
        ),
    })),
    unregisterEpisodeSelectionConsumer: (consumerId) => set((state) => {
        const nextCoordinator = unregisterEpisodeSelectionConsumerState(
            state.registeredEpisodeSelectionConsumers,
            state.episodeSelectionBusy,
            consumerId
        );

        return {
            ...state,
            registeredEpisodeSelectionConsumers: nextCoordinator.registeredConsumerIds,
            episodeSelectionBusy: nextCoordinator.busy,
        };
    }),
    acknowledgeEpisodeSelectionConsumer: (revision, consumerId) => set((state) => ({
        ...state,
        episodeSelectionBusy: acknowledgeEpisodeSelectionTransaction(
            state.episodeSelectionBusy,
            revision,
            consumerId
        ),
    })),
    setIntervalPreset: (preset) => set((state) => ({
        ...state,
        ...getPresetWeekInterval(preset)
    })),
    setIntervalWeeks: ({startWeek, endWeek}) => set((state) => ({
        ...state,
        ...normalizeWeekRange(startWeek, endWeek)
    })),
}))
